// src/components/CardPaymentOption.jsx
import React, { useState } from "react";
import { toast } from "react-toastify";
import PaymentMethodSelector from "./PaymentMethodSelector";
import PaypalPayment from "./PaypalPayment";
import { generateAndSendInvoice } from "../Services/InvoiceService";
import { createCheckoutSession } from "../Services/CheckoutService";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase";
import AlertDialog from "../Components/AlertDialog"; // Adjust the path based on your project structure

function CardPaymentOption({ details, price, items }) {
  const [paymentOption, setPaymentOption] = useState("visa");
  const [showPaypal, setShowPaypal] = useState(false);
  const [total, totalPrice, discount] = price;

  const validateFields = () => {
    const fields = {
      name: "Name",
      email: "Email",
      address: "Address",
      country: "Country",
      state: "State",
      zip: "Postal Code",
      carNumber: "Car Number",
      model: "Model",
      regDate: "Reg Date",
      phoneNumber: "Phone Number",
    };

    const missingFields = [];
    
    for (let field in fields) {
      if (!details[field]) {
        missingFields.push(fields[field]);
      }
    }
    
    if (missingFields.length > 0) {
      // Show alert for missing fields
      AlertDialog(
        "Missing Information",
        `Please fill in the following fields: ${missingFields.join(", ")}`,
        "warning",
        0, // No auto-close
        true,
        false,
        "OK",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      toast.error(`Please complete all required fields.`);
      return false;
    }
    return true;
  };

  // Save the order data to Firestore in the "Orders" collection
  const saveOrder = async (paymentMethod, extraData = {}) => {
    try {
      // Show processing alert
      AlertDialog(
        "Processing Order",
        "Please wait while we save your order...",
        "info",
        1500,
        false,
        false,
        "",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      const orderData = {
        details,
        items,
        price,
        paymentMethod,
        createdAt: new Date(),
        ...extraData,
      };
      await addDoc(collection(db, "Orders"), orderData);
      
      // Show success alert
      AlertDialog(
        "Order Saved",
        "Your order has been saved successfully!",
        "success",
        1500,
        false,
        false,
        "",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      toast.success("Order saved successfully.");
    } catch (error) {
      console.error("Error saving order:", error);
      
      // Show error alert
      AlertDialog(
        "Error",
        "There was an error saving your order. Please try again.",
        "error",
        0, // No auto-close
        true,
        false,
        "OK",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      toast.error("There was an error saving your order.");
    }
  };

  const handlePayment = async () => {
    if (!validateFields()) return;

    if (paymentOption === "paypal") {
      setShowPaypal(true);
      
      // Show PayPal selection alert
      AlertDialog(
        "PayPal Selected",
        "You'll be redirected to complete your payment with PayPal.",
        "info",
        2000,
        false,
        false,
        "",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      toast.success("Payment option selected: PayPal");
    } else {
      try {
        // Show card processing alert
        AlertDialog(
          "Processing Payment",
          `Preparing your ${paymentOption.toUpperCase()} payment of $${total}...`,
          "info",
          2000,
          false,
          false,
          "",
          "",
          () => {}, // Empty function to avoid 'not a function' error
          null
        );
        
        await generateAndSendInvoice(details, items, price);
        // Save order details for card payment before redirecting
        await saveOrder("card");
        
        // Show redirect alert
        AlertDialog(
          "Redirecting",
          "You are being redirected to our secure payment gateway...",
          "success",
          1500,
          false,
          false,
          "",
          "",
          () => {}, // Empty function to avoid 'not a function' error
          null
        );
        
        const url = await createCheckoutSession(items, totalPrice);
        
        // Short delay before redirect for user to see the alert
        setTimeout(() => {
          window.location.href = url;
        }, 1800);
        
      } catch (error) {
        console.error("Payment error", error);
        
        // Show payment error alert
        AlertDialog(
          "Payment Error",
          "There was an error processing your payment. Please try again or contact support.",
          "error",
          0, // No auto-close
          true,
          false,
          "OK",
          "",
          () => {}, // Empty function to avoid 'not a function' error
          null
        );
        
        toast.error("Payment error occurred.");
      }
    }
  };

  const handlePaypalSuccess = async (paypalDetails) => {
    console.log("PayPal Payment Successful: ", paypalDetails);
    try {
      // Show processing alert
      AlertDialog(
        "Payment Successful",
        "Your PayPal payment was successful! Generating your invoice...",
        "success",
        2000,
        false,
        false,
        "",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      await generateAndSendInvoice(details, items, price);
      // Save order details for PayPal payment along with PayPal transaction data
      await saveOrder("paypal", { paypalDetails });
      setShowPaypal(false);
      
      // Show completion alert
      AlertDialog(
        "Order Complete",
        "Thank you for your purchase! Your invoice has been sent to your email.",
        "success",
        0, // No auto-close
        true,
        false,
        "Done",
        "",
        () => {
          // Optional: Navigate to a thank you page
          // window.location.href = "/thank-you";
        },
        null
      );
      
      toast.success("Payment completed successfully via PayPal!");
      // Optionally navigate to a confirmation page.
    } catch (error) {
      console.error("Invoice error", error);
      
      // Show invoice error alert
      AlertDialog(
        "Invoice Error",
        "Payment was successful, but there was an error generating your invoice. Our team will contact you.",
        "warning",
        0, // No auto-close
        true,
        false,
        "OK",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
      
      toast.error("There was an error processing your PayPal invoice.");
    }
  };

  const handlePaypalError = (error) => {
    console.error("PayPal Payment Error: ", error);
    
    // Show PayPal error alert
    AlertDialog(
      "PayPal Error",
      "There was an error processing your PayPal payment. Please try again or choose another payment method.",
      "error",
      0, // No auto-close
      true,
      false,
      "OK",
      "",
      () => {}, // Empty function to avoid 'not a function' error
      null
    );
    
    toast.error("There was an error processing your PayPal payment.");
  };

  // Function to show payment method info when changed
  const handlePaymentMethodChange = (method) => {
    setPaymentOption(method);
    
    // Show different alerts based on payment method
    if (method === "paypal") {
      AlertDialog(
        "PayPal",
        "PayPal allows you to pay securely without sharing your financial information.",
        "info",
        2000,
        false,
        false,
        "",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
    } else if (method === "visa" || method === "mastercard") {
      AlertDialog(
        `${method.charAt(0).toUpperCase() + method.slice(1)}`,
        "Your card details will be securely processed through our payment gateway.",
        "info",
        2000,
        false,
        false,
        "",
        "",
        () => {}, // Empty function to avoid 'not a function' error
        null
      );
    }
  };

  // Function to confirm payment before processing
  const confirmPayment = () => {
    AlertDialog(
      "Confirm Payment",
      `You are about to make a payment of $${total} using ${paymentOption === "paypal" ? "PayPal" : paymentOption.toUpperCase()}.
      
      Would you like to proceed?`,
      "warning",
      0, // No auto-close
      true,
      true,
      "Yes, proceed",
      "Cancel",
      () => {
        // Proceed with payment
        handlePayment();
      },
      () => {
        // User canceled - no action needed
      }
    );
  };

  return (
    <div>
      <h4 className="mb-3 text-center">Payment</h4>
      <PaymentMethodSelector
        paymentOption={paymentOption}
        setPaymentOption={handlePaymentMethodChange}
      />
      <button
        className="btn btn-primary fw-bold btn-lg btn-block"
        type="button"
        style={{ fontSize: "15px" }}
        onClick={confirmPayment} // Changed to confirmation function
      >
        Pay Now
      </button>
      {showPaypal && paymentOption === "paypal" && (
        <PaypalPayment
          totalPrice={totalPrice}
          onSuccess={handlePaypalSuccess}
          onError={handlePaypalError}
          onClick={handlePayment}
          details={details}
        />
      )}
    </div>
  );
}

export default CardPaymentOption;